import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import shareIcon from "../../../assets/images/share.png";
import closeIcon from "../../../assets/images/close.png";
import arrowIconWhite from "../../../assets/images/arrowIconWhite.png";

import Img01 from "../../../assets/images/loghi 2x/sezione-05-popup-07-GPTW.png";
import Img02 from "../../../assets/images/loghi 2x/sezione-05-popup-07-Rina.png";
import Img03 from "../../../assets/images/logo_wellbeing.webp";

function Card({ children, img, imgAlt = "", report, href }) {
  const hasImg = Boolean(img);

  return (
    <div className="bg-white rounded-[28px] border border-[#0E3761]/10 p-6 md:p-8">
      <div className="flex flex-col gap-4 h-full items-start">

        {/* Logo al posto del titolo */}
        {hasImg && (
          <img
            src={img}
            alt={imgAlt || ""}
            className="h-[110px] object-contain"
            loading="lazy"
          />
        )}

        {children}

        <div className="pt-2 mt-auto">
          <a href={href} target="_blank" rel="noopener">
              <button
                className="bt-secondary2"
              >
                <img
                  src={arrowIconWhite}
                  alt="Arrow"
                  className="inline-block mr-2 w-[20px] h-[20px]"
                />
                {report || "Consulta la Relazione di Impatto – cap. 8.1"}
              </button>
            </a>
        </div>
      </div>
    </div>
  );
}


export default function Care({ isOpen, onClose }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const onShare = (popupId) => {
    const url = new URL(window.location.href);
    url.searchParams.set("popup", popupId);

    if (navigator.share) {
      navigator
        .share({ title: "Taking care of us", url: url.toString() })
        .catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url.toString()).catch(() => {});
    }
  };

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[1400] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-3 md:p-6"
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Taking care of us"
    >
      <div
        className="relative w-full md:w-[1280px] bg-white max-h-[90vh] overflow-y-auto overscroll-contain"
        style={{ background: "#E7E3DF", WebkitOverflowScrolling: "touch" }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-3 z-10 flex items-center gap-2 justify-end pr-3">
          <button
            onClick={() => onShare("Care")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#0E3761] bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
            title="Condividi"
            aria-label="Condividi"
          >
            <img src={shareIcon} alt="" className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#0E3761] bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
            title="Chiudi"
            aria-label="Chiudi"
          >
            <img src={closeIcon} alt="Chiudi" className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          <h2 className="text-[#5188A1] text-[30px] md:text-[40px] font-bold">
            Taking care of us
          </h2>

          {/* Griglia card: 1 col mobile, 2 col desktop */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* Card 1 */}
            <Card img={Img01} imgAlt="Great Place To Work" report="Consulta la Relazione di Impatto - cap.7.4" href="https://www.realegroup.eu/groupshare/0/IT/ListaReportsAndAccounts/87/BILANCIO%20CONSOLIDATO%20RG%202025.pdf#page=388">
              <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                Anche nel 2025 Reale Group ha ottenuto la certificazione GPTW®,
                tassello fondamentale nella People Strategy del Gruppo e
                importante canale di ascolto per mantenere viva la concretezza del
                nostro Purpose.
              </p>
            </Card>

            {/* Card 2 */}
            <Card img={Img02} imgAlt="RINA Parità di Genere" report="Consulta il Report - cap. 3 • par. 3.2.4" href="https://www.realegroup.eu/groupshare/0/IT/ListaReportsAndAccounts/87/BILANCIO%20CONSOLIDATO%20RG%202025.pdf#page=167">
              <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                Nel corso dell’esercizio le iniziative condotte dal Gruppo in
                ambito Diversity, Equity &amp; Inclusion hanno portato a estendere
                la Certificazione per la parità di genere, già ottenuta nel 2024
                per Società Reale Mutua di Assicurazioni, Italiana Assicurazioni e
                Blue Assistance, anche ad altre Società del Gruppo (Reale Immobili,
                Banca Reale, Blue Health Center e Reale Ites).
              </p>
            </Card>

            {/* Card 3 – full width (ex Card 4) */}
            <div className="md:col-span-2">
              <Card img={Img03} imgAlt="Reale Wellbeing" report="Consulta il Report - cap. 3 • par. 3.2.4" href="https://www.realegroup.eu/groupshare/0/IT/ListaReportsAndAccounts/87/BILANCIO%20CONSOLIDATO%20RG%202025.pdf#page=167">
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  Introduzione del servizio Family Manager, che accompagna i
                  genitori nell’accesso a supporti concreti (orientamento allo
                  studio, babysitting, vacanze studio).
                </p>
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  Integrazioni economiche per il congedo parentale e aumento dei
                  giorni di paternità retribuiti.
                </p>
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  Banca del Tempo Solidale, per favorire la solidarietà tra
                  colleghi che necessitano di assentarsi per finalità di cura.
                </p>
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  Per le persone che intraprendono percorsi di protezione in
                  seguito a situazioni di violenza è stato esteso a 180 giorni,
                  nell’arco di tre anni, il congedo retribuito dall’INPS.
                </p>
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  Programma Wellbeing EDU, che include webinar e laboratori (come
                  quelli gestiti con Slow Food) per promuovere scelte alimentari
                  sostenibili e sane.
                </p>
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  Ristrutturazione degli spazi (progetto New Ways of Working) per
                  renderli fattori abilitanti di collaborazione e benessere.
                </p>
                <p className="text-[#0E3761] text-[16px] md:text-[24px] leading-[1.1em] font-semibold">
                  Riprogettazione dell’offerta di cibo nelle sedi, inclusa
                  l’installazione di nuove vending machine con cibi sani e offerta
                  snack adatti a persone con particolari esigenze alimentari.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
